import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useResetPassword } from '@/api/hooks/usePasskeys';
import { ApiError } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { LogoFull } from '@/components/LogoFull';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { extractValidationErrors } from '@/utils/errorHandler';
import { trackAnonymous } from '@/components/PostHogTracker';

export function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [faxNumber, setFaxNumber] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [generalError, setGeneralError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const resetPasswordMutation = useResetPassword();
    const navigate = useNavigate();
    const { isAuthenticated, logout } = useAuth();
    const isSubmitting = resetPasswordMutation.isPending;

    useEffect(() => {
        if (!token) {
            setGeneralError('Invalid or missing reset token. Please request a new password reset.');
        }
    }, [token]);

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        setErrors({});
        setGeneralError(null);

        // Client-side validation
        const newErrors: Record<string, string> = {};

        if (!password) {
            newErrors.password = 'Password is required';
        } else if (password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        } else if (!/[A-Z]/.test(password)) {
            newErrors.password = 'Password must contain an uppercase letter';
        } else if (!/[a-z]/.test(password)) {
            newErrors.password = 'Password must contain a lowercase letter';
        } else if (!/\d/.test(password)) {
            newErrors.password = 'Password must contain a number';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            await resetPasswordMutation.mutateAsync({ token, password, faxNumber });
            if (isAuthenticated) {
                try {
                    await logout();
                } catch {
                    // The password reset already succeeded, so a failed logout must not hide that.
                }
            }
            trackAnonymous('reset_password_completed');
            setSuccess(true);
        } catch (err) {
            if (err instanceof ApiError) {
                const validationErrors = extractValidationErrors(err);
                if (Object.keys(validationErrors).length > 0) {
                    setErrors(validationErrors);
                } else {
                    const errorMsg =
                        typeof err.data === 'object' && err.data && 'error' in err.data
                            ? String((err.data as { error: string }).error)
                            : err.message;
                    if (errorMsg.includes('invalid') || errorMsg.includes('expired')) {
                        setGeneralError(
                            'The password reset link is invalid or has expired. Please request a new one.',
                        );
                    } else {
                        setGeneralError(errorMsg || 'Failed to reset password. Please try again.');
                    }
                }
            } else {
                setGeneralError('An unexpected error occurred');
            }
        }
    }

    if (success) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/">
                            <LogoFull className="h-10 w-auto mx-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Password reset successful
                        </h1>

                        <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                            Your password has been reset successfully. You can now log in with your new password.
                        </div>

                        <Button
                            type="button"
                            className="w-full"
                            size="lg"
                            onClick={() => navigate('/login', { replace: true })}
                        >
                            Go to login
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <LogoFull className="h-10 w-auto mx-auto" />
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                    <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                        Reset your password
                    </h1>
                    <p className="mb-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        Enter your new password below.
                    </p>

                    {generalError && (
                        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                            {generalError}
                            {generalError.includes('expired') && (
                                <div className="mt-2">
                                    <Link
                                        to="/forgot-password"
                                        className="font-medium underline hover:no-underline"
                                    >
                                        Request a new reset link
                                    </Link>
                                </div>
                            )}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <input
                            type="text"
                            name="fax_number"
                            value={faxNumber}
                            onChange={(e) => setFaxNumber(e.target.value)}
                            autoComplete="new-password"
                            tabIndex={-1}
                            style={{ position: 'absolute', left: '-9999px' }}
                            aria-hidden="true"
                        />
                        <Input
                            type="password"
                            id="password"
                            label="New Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="Create a strong password"
                            error={errors.password}
                            helperText={!errors.password ? '8+ characters with uppercase, lowercase, and number' : undefined}
                            disabled={!token}
                        />

                        <Input
                            type="password"
                            id="confirmPassword"
                            label="Confirm New Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            placeholder="Re-enter your new password"
                            error={errors.confirmPassword}
                            disabled={!token}
                        />

                        <Button
                            type="submit"
                            disabled={isSubmitting || !token}
                            isLoading={isSubmitting}
                            className="w-full"
                            size="lg"
                        >
                            {isSubmitting ? 'Resetting...' : 'Reset password'}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <Link
                            to={isAuthenticated ? '/profile' : '/login'}
                            className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                            {isAuthenticated ? 'Cancel' : 'Back to login'}
                        </Link>
                    </div>
                </div>

                <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    Securely manage your financial data
                </p>
            </div>
        </div>
    );
}
